/**
 * Почему у строки ведомости пусто вместо суммы.
 *
 * `accrued_minor: null` — не ноль, и разница здесь не косметическая: ноль означал бы
 * «заработал нисколько», а пусто значит «посчитать нечем». Строка с флагом не входит в
 * `totals` и учтена в `skipped_rows`, поэтому экран обязан не просто показать прочерк,
 * а сказать ПОЧЕМУ и куда идти — иначе «итого» выглядит полным, когда у пятерых из
 * тридцати оклад не заведён.
 *
 * Разбор идёт по строкам, а не по union: незнакомый флаг с более нового сервера должен
 * деградировать до «не посчитано», а не ронять таблицу.
 */
import type {PayrollFlag} from '@/entities/salary/types';

const KNOWN: ReadonlyArray<PayrollFlag> = [
  'noSalary',
  'noSchedule',
  'mixedCurrency',
];

/** Флаг, который сервер прислал строкой, → известный флаг, либо null. */
export function knownFlag(flag: string): PayrollFlag | null {
  return (KNOWN as readonly string[]).includes(flag) ? (flag as PayrollFlag) : null;
}

/** Первый понятный флаг строки — тот, который и объясняет пустую сумму. */
export function primaryFlag(flags: readonly string[]): PayrollFlag | null {
  for (const flag of flags) {
    const known = knownFlag(flag);
    if (known) {
      return known;
    }
  }
  return null;
}

/**
 * Ключ подписи для флага. Незнакомый флаг получает общий текст «не посчитано» — он
 * честен, а придумывать объяснение за сервер нельзя.
 */
export function flagLabelKey(flag: string): string {
  return knownFlag(flag) ? `payroll.flag.${flag}` : 'payroll.flag.unknown';
}

/** Куда ведёт починка: «завести оклад» — в карточку человека, «график» — в отдел. */
export type FlagRemedy = 'salary' | 'schedule' | 'none';

export function flagRemedy(flag: string | null): FlagRemedy {
  switch (flag) {
    case 'noSalary':
      return 'salary';
    case 'noSchedule':
      return 'schedule';
    default:
      return 'none';
  }
}
